import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

/**
 * GET /me/notifications — paginated in-app feed + unread count.
 * Includes a cheaper unread-count poll at GET /me/notifications/unread-count
 * for the navbar bell.
 */
export function useNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread]               = useState(0);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState('');

  const fetch = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await api.get('/me/notifications');
      setNotifications(data?.data ?? []);
      setUnread(data?.unread_count ?? 0);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load notifications.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const markRead = async (id) => {
    await api.post(`/me/notifications/${id}/mark-read`);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
    setUnread(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await api.post('/me/notifications/mark-all-read');
    setNotifications(prev => prev.map(n => n.read_at ? n : { ...n, read_at: new Date().toISOString() }));
    setUnread(0);
  };

  return { notifications, unread, loading, error, refetch: fetch, markRead, markAllRead };
}

/**
 * Lightweight unread-only poll for the navbar bell — avoids fetching the
 * full notification list on every page load.
 */
export function useUnreadCount(pollMs = 0) {
  const [unread, setUnread] = useState(0);

  const fetch = useCallback(async () => {
    try {
      const { data } = await api.get('/me/notifications/unread-count');
      setUnread(data?.unread_count ?? 0);
    } catch { /* swallow — bell just shows 0 */ }
  }, []);

  useEffect(() => {
    fetch();
    if (!pollMs) return;
    const id = setInterval(fetch, pollMs);
    return () => clearInterval(id);
  }, [fetch, pollMs]);

  return { unread, refetch: fetch, setUnread };
}

/**
 * GET /me/tier — current tier, next threshold, full ladder, progress math.
 */
export function useTier() {
  const [tier, setTier]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/me/tier');
        setTier(data);
      } catch (err) {
        setError(err.response?.data?.message || 'Could not load tier info.');
      } finally { setLoading(false); }
    })();
  }, []);

  return { tier, loading, error };
}

/**
 * GET /me/referrals + POST /me/referrals/regenerate.
 */
export function useReferrals() {
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const fetch = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await api.get('/me/referrals');
      // Server's share_url is built from APP_FRONTEND_URL which may be unset
      // in some environments. Construct from the live origin instead so the
      // link always lands on the right domain (dev/stg/prod or local).
      if (data?.code && typeof window !== 'undefined') {
        data.share_url = `${window.location.origin}/r/${data.code}`;
      }
      setStats(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load referrals.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const regenerate = async () => {
    const { data } = await api.post('/me/referrals/regenerate');
    const shareUrl = typeof window !== 'undefined'
      ? `${window.location.origin}/r/${data.code}`
      : data.share_url;
    setStats(prev => prev ? { ...prev, code: data.code, share_url: shareUrl } : prev);
    return { ...data, share_url: shareUrl };
  };

  return { stats, loading, error, refetch: fetch, regenerate };
}
